import { getPendingKpis, deletePendingKpi } from "../services/offlineStorage";
import { useOfflineKpiSubmit } from "./kpi";
import { useRef, useCallback } from "react";

export function useSyncOfflineData() {
  const { submitOfflineKpi } = useOfflineKpiSubmit();
  
  const isSyncInProgress = useRef(false);
  const syncedIds = useRef(new Set());

  const syncOfflineData = useCallback(async (isOnline) => {
    if (isSyncInProgress.current) {
      return;
    }

    try {
      isSyncInProgress.current = true;
      const pendingActions = await getPendingKpis();
      
      if (isOnline && pendingActions && pendingActions.length > 0) {
        console.log('📦 Pending offline actions:', pendingActions);
        
        const unprocessedActions = pendingActions.filter(({ id }) => !syncedIds.current.has(id));
        
        if (unprocessedActions.length === 0) {
          console.log('All pending actions have already been processed');
          return;
        }
        
        console.log(`Processing ${unprocessedActions.length} new actions`);
        
        await Promise.all(unprocessedActions.map(async ({ action, id }) => {
          try {
            syncedIds.current.add(id);
            let processedData = action.data;
            if (action.data.imageURL) {
              const response = await fetch(action.data.imageURL);
              const blob = await response.blob();
              const file = new File([blob], "image.jpg", { type: blob.type });
              const formData = new FormData();
              formData.append("text", action.data.text);
              formData.append("image", file);
              formData.append("imageKey", action.data.imageKey);
              formData.append("amount", action.data.amount);

              processedData = formData;
            }
            
            try {
              await submitOfflineKpi(
                action.kpiId,
                action.userId,
                action.month,
                processedData,
                action.companyId,
                id,
                action.entryId
              );
            } catch (error) {
              // Check if this is a duplicate entry error
              if (error?.error === "An entry with this ID already exists") {
                console.log(`Entry already exists, skipping: ${error?.entryId}`);
                await deletePendingKpi(id);
                syncedIds.current.delete(id);
              }
            }
            
            // Delete the pending KPI regardless of success or duplicate error
            await deletePendingKpi(id);
          } catch (error) {
            syncedIds.current.delete(id);
            console.error(`Failed to process offline action ${id}:`, error);
          }
        }));
      }
    } finally {
      isSyncInProgress.current = false;
    }
  }, [submitOfflineKpi]);

  return { syncOfflineData };
}
